import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LazyLoadImage } from "react-lazy-load-image-component";
import shower from "../../../assets/icon.svg";
import bedroom from "../../../assets/icon1.svg";
import squareMeters from "../../../assets/SquareMeters.svg";
import Googlemap2 from "../GoogleMap/Googlemap2";
import isEmpty from "../utils/isEmpty";
import { FiChevronDown, FiChevronRight } from "react-icons/fi";
import { CurrencySymbol } from "../../../utils/enums";
import {
  getPropertyStatus,
  isSoldProperty,
  matchesPropertyFilter,
  mergeAgencyProperties,
  withinPropertyPeriod,
} from "../../../utils/propertyFilters";

const SORT_TAGS = ["Sold", "For sale", "Leased", "For rent"];
const PERIOD_OPTIONS = [1, 6, 12];

const periodLabel = (months) =>
  `${months} ${months === 1 ? "month" : "months"}`;

const PeriodDropdown = ({ value, onChange, options }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    const onOutside = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, []);

  useEffect(() => {
    const onEscape = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onEscape);
    return () => document.removeEventListener("keydown", onEscape);
  }, []);

  return (
    <div ref={rootRef} className="relative z-30 w-full sm:w-auto sm:min-w-[148px]">
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => setOpen((prev) => !prev)}
        className="flex w-full items-center justify-between gap-3 rounded-full border-[1.5px] border-[#EAEAEA] bg-white py-2 pl-5 pr-4 text-xs font-light text-[#000929] outline-none transition-colors hover:border-[#000929]/40 md:text-base"
      >
        <span>{periodLabel(value)}</span>
        <FiChevronDown
          className={`h-4 w-4 shrink-0 text-[#525252] transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <ul
          role="listbox"
          aria-label="Time period"
          className="absolute right-0 top-[calc(100%+8px)] w-full min-w-[148px] overflow-hidden rounded-2xl border border-[#EAEAEA] bg-white py-1.5 shadow-[0px_4px_20px_rgba(0,0,0,0.12)]"
        >
          {options.map((months) => (
            <li key={months} role="option" aria-selected={value === months}>
              <button
                type="button"
                onClick={() => {
                  onChange(months);
                  setOpen(false);
                }}
                className={`w-full px-5 py-2.5 text-left text-xs transition-colors md:text-base ${value === months
                    ? "bg-[#097FE6] font-medium text-white"
                    : "font-light text-[#000929] hover:bg-[#F3F6F9]"
                  }`}
              >
                {periodLabel(months)}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const toCount = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const FILTER_COPY = {
  Sold: { summaryLabel: "sold", countLabel: "sold" },
  "For sale": { summaryLabel: "for sale", countLabel: "for sale" },
  Leased: { summaryLabel: "leased", countLabel: "leased" },
  "For rent": { summaryLabel: "for rent", countLabel: "for rent" },
};

const PropertiesCard = ({
  AgencyData,
  property,
  MarketPerformanceData,
  agencyId,
  recentlySoldProperties = [],
}) => {
  const navigate = useNavigate();
  const [activeTag, setActiveTag] = useState("Sold");
  const [periodMonths, setPeriodMonths] = useState(12);
  const [showAll, setShowAll] = useState(false);

  const agency = AgencyData?.[0] ?? {};

  const allProperties = useMemo(() => {
    const fromProfile = Array.isArray(property)
      ? property
      : Array.isArray(agency?.properties)
        ? agency.properties
        : [];
    return mergeAgencyProperties(
      fromProfile,
      recentlySoldProperties,
      agencyId ?? agency?._id ?? agency?.id,
    );
  }, [property, agency?.properties, agency?._id, agency?.id, recentlySoldProperties, agencyId]);

  const market = MarketPerformanceData ?? {};
  const sales = market?.sales ?? {};
  const rent = market?.rent ?? {};
  const summary = market?.summary ?? {};
  const headerPeriodMonths = toCount(market?.periodMonths) || 12;

  const summaryCounts = useMemo(() => {
    const fromMarket = {
      sold: toCount(summary?.propertiesSold ?? sales?.propertiesSold),
      forSale: toCount(sales?.propertiesForSale),
      leased: toCount(summary?.propertiesLeased ?? rent?.propertiesLeased),
      forRent: toCount(rent?.propertiesForLease),
    };
    const hasMarket =
      fromMarket.sold +
      fromMarket.forSale +
      fromMarket.leased +
      fromMarket.forRent >
      0;
    if (hasMarket) return fromMarket;
    return {
      sold: allProperties.filter((p) => matchesPropertyFilter(p, "Sold")).length,
      forSale: allProperties.filter((p) => matchesPropertyFilter(p, "For sale"))
        .length,
      leased: allProperties.filter((p) => matchesPropertyFilter(p, "Leased"))
        .length,
      forRent: allProperties.filter((p) => matchesPropertyFilter(p, "For rent"))
        .length,
    };
  }, [allProperties, summary, sales, rent]);

  const filteredProperties = useMemo(() => {
    return allProperties.filter(
      (item) =>
        matchesPropertyFilter(item, activeTag) &&
        withinPropertyPeriod(item, periodMonths, activeTag),
    );
  }, [allProperties, activeTag, periodMonths]);

  const periodInitializedRef = useRef(false);
  useEffect(() => {
    const fromApi = toCount(market?.periodMonths);
    if (
      !periodInitializedRef.current &&
      fromApi &&
      PERIOD_OPTIONS.includes(fromApi)
    ) {
      setPeriodMonths(fromApi);
      periodInitializedRef.current = true;
    }
  }, [market?.periodMonths]);

  useEffect(() => {
    setShowAll(false);
  }, [activeTag, periodMonths]);

  const displayedProperties = showAll
    ? filteredProperties
    : filteredProperties.slice(0, 3);
  const displayedCount = displayedProperties.length;
  const totalCount = filteredProperties.length;
  const hasMore = totalCount > 3;

  const formatPrice = (item) => {
    if (item?.Hide_the_price_and_display_contact_agent) return "Contact agent";
    if (
      item?.price_display_checked === "show_actual_price" &&
      item?.price != null
    ) {
      const n = Number(item.price);
      if (!Number.isNaN(n) && n > 0) return `$${n.toLocaleString()}`;
    }
    if (item?.price_display) return item.price_display;
    const p = item?.price ?? item?.display_price;
    if (p == null || p === "") return "Price on request";
    const n = Number(p);
    if (!Number.isNaN(n) && n > 0) return `${CurrencySymbol.NGN} ${n.toLocaleString()}`;
    return String(p).startsWith(CurrencySymbol.NGN) ? String(p) : `${CurrencySymbol.NGN}${p}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  const getStatusBadge = (item) => {
    const status = getPropertyStatus(item);
    const date =
      item?.sold_date ??
      item?.soldDate ??
      item?.leased_date ??
      item?.createdAt;
    if (status === "sold" || isSoldProperty(item))
      return date ? `Sold on ${formatDate(date)}` : "Sold";
    if (status === "active" || status === "for sale") return "For sale";
    if (status === "leased")
      return date ? `Leased on ${formatDate(date)}` : "Leased";
    if (status === "rent" || status === "for rent")
      return date ? `Listed on ${formatDate(date)}` : "For rent";
    const label = item?.status ?? item?.status_type ?? "Listing";
    return label;
  };

  const getImageBadge = (item) => {
    const status = getPropertyStatus(item);
    if (status === "sold" || isSoldProperty(item)) return "Sold";
    if (status === "active" || status === "for sale") return "For sale";
    if (status === "leased") return "Leased";
    if (status === "rent" || status === "for rent") return "For rent";
    return item?.status ?? "Listing";
  };

  const propertyImg = (item) => {
    const raw = item?.frontPageImg ?? item?.florePlansImg ?? item?.images;
    return Array.isArray(raw) ? raw[0] : raw;
  };

  const bathroomsDisplay = (item) => {
    const b = item?.Bathrooms;
    if (b == null || b === "") return "–";
    if (typeof b === "string" && b.includes("+")) return b.split("+")[1] ?? b;
    return b;
  };

  const getArea = (item) => {
    const size = item?.land_size ?? item?.house_size ?? item?.square_area;
    if (size != null && size !== "") return `${size} m²`;
    return "–";
  };

  const agencyName =
    agency?.principal_name || agency?.agency_name || "This agency";
  const filterCopy = FILTER_COPY[activeTag] ?? FILTER_COPY.Sold;
  const resPonce = isEmpty(allProperties);

  if (!AgencyData?.length) return null;

  return (
    <div className="bg-white rounded-2xl p-6">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <div className="w-full flex justify-between sm:justify-start items-center gap-3">
            <div className="text-[#000929] font-semibold text-2xl">
              Our properties
            </div>
          </div>
          <div className="text-[#000929] font-normal text-base">
            {agencyName} have sold {summaryCounts.sold} properties in the last{" "}
            {headerPeriodMonths} months on realestate.com.ng, have{" "}
            {summaryCounts.forSale} properties for sale and have{" "}
            {summaryCounts.forRent} properties for rent.
          </div>
          <div className="w-full flex flex-col md:flex-row justify-between mt-2 gap-3">
            <div className="flex flex-wrap gap-2 justify-start items-center">
              {SORT_TAGS.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => setActiveTag(tag)}
                  className={`round !text-[#000929] text-xs md:text-base outline-none rounded-full py-2 px-5 cursor-pointer ${activeTag === tag
                    ? "border-[2px] border-[#000929] font-semibold"
                    : "border-[1.5px] border-[#EAEAEA]"
                    }`}
                >
                  {tag}
                </button>
              ))}
            </div>
            <div className="md:block">
              <select
                name="period"
                value={periodMonths}
                onChange={(e) => setPeriodMonths(Number(e.target.value))}
                className="round flex items-center justify-center w-full font-light !text-[#000929] text-xs md:text-base outline-none border-[1.5px] border-[#EAEAEA] rounded-full py-2 px-5 cursor-pointer"
              >
                {PERIOD_OPTIONS.map((m) => (
                  <option key={m} value={m}>
                    {m} {m === 1 ? "month" : "months"}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="!relative rounded-xl overflow-hidden">
          {!resPonce && filteredProperties.length > 0 && (
            <Googlemap2
              key={`${activeTag}-${periodMonths}-${filteredProperties.length}`}
              location={filteredProperties}
            />
          )}
          {!resPonce && filteredProperties.length === 0 && (
            <div className="h-[400px] flex items-center justify-center bg-[#F3F6F9] text-[#737373] text-sm rounded-xl">
              No {filterCopy.countLabel} properties in the last{" "}
              {periodLabel(periodMonths)}.
            </div>
          )}
        </div>

        <div className="text-[#000929] font-light text-sm opacity-75">
          Showing {displayedCount} of {totalCount} properties{" "}
          {filterCopy.countLabel} on realestate.com.ng in the last{" "}
          {periodLabel(periodMonths)}
        </div>
      </div>

      <div className="mt-4">
        <div className="mt-1 flex flex-col gap-4">
          {displayedProperties.map((item, index) => (
            <div
              key={item?._id ?? item?.id ?? index}
              className="relative flex flex-col md:flex-row md:gap-4 cursor-pointer border-[1.5px] border-[#EAEAEA] rounded-xl"
              onClick={() =>
                navigate(`/property-house/${item?._id || item?.id}`)
              }
            >
              {propertyImg(item) && (
                <LazyLoadImage
                  src={propertyImg(item)}
                  alt=""
                  loading="lazy"
                  effect="blur"
                  className="rounded-t-xl rounded-b-none md:rounded-l-xl h-[210px] max-w-full md:w-[251px] md:max-w-[250px] object-cover"
                />
              )}

              <div className="absolute max-[767px]:top-4 md:bottom-2 left-2 bg-white p-1 rounded-lg flex items-center gap-1">
                <div className="w-2 h-2 p-1 rounded-full bg-[#FCCA36]" />
                <span className="text-xs font-normal">
                  {getImageBadge(item)}
                </span>
              </div>

              <div className="py-5 px-3 w-full mt-2 flex flex-col gap-3">
                <div className="flex justify-between items-center gap-2">
                  <div className="font-bold text-2xl text-[#097FE6]">
                    {formatPrice(item)}
                  </div>
                  <div className="font-semibold text-sm text-[#000929] border-[1.5px] border-[#EAEAEA] rounded-full py-1 px-2 flex items-center gap-1 shrink-0">
                    <div className="w-[7px] h-[7px] rounded-full bg-[#097FE6]" />
                    {getStatusBadge(item)}
                  </div>
                </div>
                <div className="font-medium text-[#000929] text-xl capitalize">
                  {item?.name ||
                    `${item?.street_address_number || ""} ${item?.street_address_name || ""}`.trim() ||
                    "Property"}
                </div>
                <div className="font-normal text-[#000929] text-sm opacity-50">
                  {[item?.street_address_number, item?.street_address_name]
                    .filter(Boolean)
                    .join(" ")}
                  {item?.suburb ? `, ${item.suburb}` : ""}
                </div>
                <div className="flex flex-wrap justify-start items-center gap-3">
                  <div className="flex justify-center items-center rounded-md">
                    <img src={bedroom} alt="" className="w-4 mr-2" />
                    <div className="font-medium text-sm text-[#000929] opacity-75">
                      {item?.Bedrooms ?? "–"} Beds
                    </div>
                  </div>
                  <div className="flex justify-center items-center rounded-md">
                    <img src={shower} alt="" className="w-4 mr-2" />
                    <div className="font-medium text-sm text-[#000929] opacity-75">
                      {bathroomsDisplay(item)} Bathrooms
                    </div>
                  </div>
                  <div className="flex justify-center items-center rounded-md">
                    <img src={squareMeters} alt="" className="w-4 mr-2" />
                    <div className="font-medium text-sm text-[#000929] opacity-75">
                      {getArea(item)}
                    </div>
                  </div>
                </div>
              </div>

              {agency?.agents?.[0]?.profileImg && (
                <div className="absolute right-7 bottom-4 hidden md:flex items-center gap-2">
                  <LazyLoadImage
                    src={agency.agents[0].profileImg}
                    alt=""
                    loading="lazy"
                    effect="blur"
                    className="rounded-full h-8 w-8 md:h-12 md:w-12 object-cover"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/agent-profile/${agency.agents[0]?._id}`);
                    }}
                  />
                  <FiChevronRight className="text-[#99A1AF] w-6 h-6" />
                </div>
              )}
            </div>
          ))}
        </div>

        {hasMore && (
          <div className="flex justify-center">
            <button
              type="button"
              className="border border-[#959199] rounded-lg text-base px-6 py-3 font-light text-[#000929] mt-4 mb-[10px]"
              onClick={() => setShowAll((prev) => !prev)}
            >
              {showAll ? "Show less properties" : "Show more properties"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PropertiesCard;
